import React from 'react';
import _range from 'lodash/range';
import dynamic from 'next/dynamic';

import Table from 'components/customTable';

const LEGENDS = ['Male', 'Female'];

const COLORS_CODES = ['#26A69A', '#5C6BC0'];

const ReactTooltip = dynamic(() => import('react-tooltip'), { ssr: false });

const AgeByEthnicityGender = ({
  ageByEthnicityGender,
  contentBlurb,
  jobTitle,
  jobTitleArticle,
}) => {
  const yLabels = Object.keys(ageByEthnicityGender);
  const values = Object.values(ageByEthnicityGender);

  const tableData = Object.entries(ageByEthnicityGender ?? {}).map(([key, value]) => ({
    race: key,
    male: value?.Male?.medianAge,
    female: value?.Female?.medianAge,
  }));

  const getMinMax = (data = [], field) => {
    const list = data.reduce((acc1, item1) => {
      acc1.push(
        ...Object.values(item1).reduce((acc2, item2) => {
          acc2.push(item2[field].toFixed(0));
          return acc2;
        }, [])
      );
      return acc1;
    }, []);
    return [Math.min(...list), Math.max(...list)];
  };

  const [xMin, xMax] = getMinMax(values, 'averageAge');

  let xLabels = _range(xMin - 2, xMax + 4, 2);

  // reduce xLabels numbers if there are more than 7 items.
  if (xLabels.length > 7) {
    xLabels = _range(xMin - 2, xMax + 4, 4);
  }

  const calculateBarWidth = (value) => {
    const min = xLabels[0];
    const max = xLabels[xLabels.length - 1];
    const diff = max - min;
    return ((value - min) / diff) * 100;
  };

  return (
    <section id="age-by-ethinicty-gender">
      <h2 className="brandonH2 w-730 z-mb-48">
        {`Average ${jobTitle?.toLowerCase?.()} age`}
      </h2>

      {contentBlurb && contentBlurb.content && (
        <div
          dangerouslySetInnerHTML={{
            __html: contentBlurb.content,
          }}
        />
      )}
      <div className="d-flex justify-content-center z-mt-48 legends">
        {LEGENDS.map((legend, idx) => (
          <div key={legend + idx} className="d-flex align-items-center legend">
            <div className="circle" style={{ background: COLORS_CODES[idx] }} />
            <div className="openSansBlack">{legend}</div>
          </div>
        ))}
      </div>

      <div className="mt-4 d-flex chart-container">
        <div className="d-inline-flex flex-column justify-content-between align-items-end y-labels">
          {yLabels.map((yLabel, idx) => (
            <div key={yLabel + idx} className="y-label">
              <label className="openSansBlack text-right mb-0">{yLabel}</label>
              <div className="bars">
                {Object.values(values[idx]).map(({ averageAge }, idx2) => (
                  <div
                    key={averageAge + idx2}
                    className="bar"
                    data-place="top"
                    data-tip={`${LEGENDS[idx2]}: <strong>${averageAge.toFixed(0)}</strong>`}
                    style={{
                      background: COLORS_CODES[idx2],
                      width: `${calculateBarWidth(averageAge.toFixed(0))}%`,
                    }}
                  />
                ))}
              </div>
            </div>
          ))}
        </div>

        <div className="d-flex align-items-end ml-2 x-labels">
          {xLabels.map((xLabel, idx) => (
            <div
              key={xLabel + idx}
              className="x-label"
              style={{
                width: `${100 / (xLabels.length - 1)}%`,
              }}
            >
              <label className="mb-0 lightTextStyle2">{xLabel}</label>
            </div>
          ))}
        </div>
        <ReactTooltip html />
      </div>
      <div className="mb-0 z-mt-48">
        <Table
          columnConfig={{
            race: {
              columnName: 'Race',
            },
            male: {
              columnName: 'Male Age',
            },
            female: {
              columnName: 'Female Age',
            },
          }}
          bodyData={tableData}
          defaultRowsDisplay={99}
          tableHeaderTitleContent={
            <h3 className="zp-title-h3 text-capitalize z-mb-24">{`${jobTitle} age by race and gender`}</h3>
          }
        />
      </div>
    </section>
  );
};

export default AgeByEthnicityGender;
